import { Crown, Flame, Medal, Trophy } from "lucide-react";

import { Avatar, Badge, getInitials, tintForId } from "@/ui";
import {
  useTournamentStandings,
  type PodiumEntry,
  type PodiumMember,
  type StandingPlayer,
} from "@/features/queries";

interface Props {
  tournamentId: number;
}

/** Live participants list + post-tournament podium.
 *  Rendered inside the user-facing tournament sheet under the bracket. */
export function TournamentStandings({ tournamentId }: Props) {
  const { data, isLoading } = useTournamentStandings(tournamentId);
  if (isLoading || !data) return null;
  if (data.participants.length === 0) return null;

  const isCompleted = data.status === "completed";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {isCompleted && data.podium.length > 0 && (
        <Podium entries={data.podium} />
      )}
      <div>
        <div className="section-header" style={{ padding: "0 0 8px" }}>
          {isCompleted ? "Итоги" : "Участники"}
        </div>
        <ParticipantsList players={data.participants} showPlace={isCompleted} />
      </div>
    </div>
  );
}

function Podium({ entries }: { entries: PodiumEntry[] }) {
  // Render in visual order: 2nd, 1st, 3rd — like a real podium.
  const byPlace = new Map(entries.map((e) => [e.place, e]));
  const order: (1 | 2 | 3)[] = [2, 1, 3];

  return (
    <div>
      <div className="section-header" style={{ padding: "0 0 8px" }}>
        Пьедестал
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 8,
          alignItems: "end",
        }}
      >
        {order.map((place) => {
          const e = byPlace.get(place);
          if (!e) return <div key={place} />;
          return <PodiumStep key={place} entry={e} place={place} />;
        })}
      </div>
    </div>
  );
}

function PodiumStep({ entry, place }: { entry: PodiumEntry; place: 1 | 2 | 3 }) {
  // Visual heights so 1st clearly tops the podium.
  const heights: Record<1 | 2 | 3, number> = { 1: 96, 2: 72, 3: 56 };
  const colors: Record<1 | 2 | 3, string> = {
    1: "linear-gradient(180deg, #FFD86B 0%, #F2A53C 100%)",
    2: "linear-gradient(180deg, #DDE3EC 0%, #A6B0BD 100%)",
    3: "linear-gradient(180deg, #E5B387 0%, #B47B4D 100%)",
  };
  const icons: Record<1 | 2 | 3, JSX.Element> = {
    1: <Crown size={16} color="#fff" />,
    2: <Trophy size={14} color="#fff" />,
    3: <Medal size={14} color="#fff" />,
  };

  const isPair = entry.members.length > 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div
        style={{
          display: "flex",
          // Doubles: overlap the two avatars slightly so they read as a
          // pair rather than two unrelated players on the same step.
          marginLeft: isPair ? 10 : 0,
        }}
      >
        {entry.members.map((m, i) => (
          <div
            key={m.user_id}
            style={{ marginLeft: i === 0 ? 0 : -10 }}
          >
            <Avatar
              initials={getInitials(m.first_name, m.last_name, m.username)}
              size={isPair ? 38 : 44}
              tint={tintForId(m.user_id)}
              src={m.photo_url ?? undefined}
            />
          </div>
        ))}
      </div>
      <div
        className="caption"
        style={{
          fontWeight: 600,
          textAlign: "center",
          maxWidth: "100%",
          overflow: "hidden",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          // Force wrap in doubles so both partner names show.
          wordBreak: "break-word",
          lineHeight: "1.2",
        }}
      >
        {entry.members.map(memberShort).join(" / ")}
      </div>
      <div
        style={{
          width: "100%",
          height: heights[place],
          background: colors[place],
          borderRadius: "var(--r-md) var(--r-md) 0 0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingTop: 8,
          gap: 4,
          color: "#fff",
        }}
      >
        {icons[place]}
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: 22,
            lineHeight: 1,
          }}
        >
          {place}
        </div>
      </div>
    </div>
  );
}

function memberShort(m: PodiumMember): string {
  // In doubles we have to fit two names on a podium step, so we prefer
  // first name (or username) and fall back to the full name only when
  // nothing shorter exists.
  return (
    m.first_name ||
    m.username ||
    [m.first_name, m.last_name].filter(Boolean).join(" ").trim() ||
    `player-${m.user_id}`
  );
}

function ParticipantsList({
  players,
  showPlace,
}: {
  players: StandingPlayer[];
  showPlace: boolean;
}) {
  return (
    <div
      style={{
        background: "var(--bg-section)",
        border: "1px solid var(--separator)",
        borderRadius: "var(--r-md)",
        overflow: "hidden",
      }}
    >
      {players.map((p, idx) => (
        <div
          key={p.user_id}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 12px",
            borderTop: idx === 0 ? "none" : "1px solid var(--separator)",
            background: p.is_favorite ? "rgba(255,90,31,0.06)" : "transparent",
          }}
        >
          <Avatar
            initials={getInitials(p.first_name, p.last_name, p.username)}
            size={32}
            tint={tintForId(p.user_id)}
            src={p.photo_url ?? undefined}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span
                className="body"
                style={{
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {playerLabel(p)}
              </span>
              {p.is_favorite && (
                <Flame size={14} color="var(--brand-orange)" aria-label="Фаворит" />
              )}
              {showPlace && p.place != null && (
                <Badge kind={p.place === 1 ? "brand" : "neutral"}>{p.place}</Badge>
              )}
            </div>
            <div className="caption" style={{ color: "var(--fg2)" }}>
              Рейтинг {p.rating}
              {p.played > 0 && (
                <>
                  {" · "}
                  <span style={{ color: "var(--brand-orange)", fontWeight: 600 }}>
                    {p.wins}
                  </span>
                  –{p.losses}
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function playerLabel(p: StandingPlayer): string {
  const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return name || p.username || `player-${p.user_id}`;
}
